import React, { useEffect } from 'react';
import {
  Button,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled
} from '@mui/material';
import { dateFormatter, getReccurssiveObjectKeys } from '../../../../../../utils';
import { Checkbox } from '../../../../../../components';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(0.9),
  whiteSpace: 'nowrap',
  fontSize: '15px',
  fontWeight: 500,
  maxWidth: 150, 
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'default',
  userSelect: 'text',
}));

export const SelectDoctorTable = ({
  data,
  columns,
  onRowClick,
  containerSX,
  sx,
  size,
  showDeleteAction,
  handleRowSelect,
  isSelected,
  showOk,
  currentAction,
  showDone,
  handleDone,
  handleDefer,
  showRevert,
  handleRevert,
  showAuthorize,
  onAuthorizeHandler,
  cancelAppointment,
  showInputField,
  showInputFields,
  handleInputChange,
  inputValues,
  loading,
  onSumChange,
  selectedRows
}) => {
  
  useEffect(() => {
    if (typeof onSumChange !== 'function') {
      return;
    }
    const totalAmount = data.reduce((sum, row) => {
      const returnQty = parseFloat(inputValues[row._id]?.['Return_Qty'] || 0);
      const rate = parseFloat(row.mrp || 0);
      return sum + returnQty * rate;
    }, 0);
    onSumChange(totalAmount);
  }, [data, inputValues, onSumChange]);


  return (
    <TableContainer sx={{ maxHeight: '250px', ...containerSX }}>
      <MuiTable sx={sx} size={size || 'medium'}>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'lightblue' }}>
            <StyledTableCell key="sr.no.-cell" sx={{ fontWeight: 'bold' }}>
              Sr. No.
            </StyledTableCell>
            {columns.map((column, index) => (
              <StyledTableCell sx={{ fontWeight: 'bold' }} key={`header-cell-${index}`}>
                {column.header}
              </StyledTableCell>
            ))}
            {showDeleteAction && (
              <StyledTableCell key="action-cell" sx={{ fontWeight: 'bold' }}>
                Action
              </StyledTableCell>
            )}
            {showAuthorize && (
              <StyledTableCell key="authorize-cell" sx={{ fontWeight: 'bold' }}>
                Authorize
              </StyledTableCell>
            )}
            {showOk && (
              <>
                <StyledTableCell key="ok-cell" sx={{ fontWeight: 'bold' }}>
                  Select
                </StyledTableCell>
              </>
            )}
            {showDone && (
              <>
                <StyledTableCell key="done-cell" sx={{ fontWeight: 'bold' }}>
                  Done/Defer
                </StyledTableCell>
              </>
            )}
            {showRevert && (
              <>
                <StyledTableCell key="revert-cell" sx={{ fontWeight: 'bold' }}>
                  Revert
                </StyledTableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, rowIndex) => (
            <TableRow
              {...(onRowClick && {
                onClick: () => {
                  onRowClick(row);
                }
              })}
              sx={{
                backgroundColor: rowIndex % 2 ? 'grey.200' : 'none',
                cursor: onRowClick ? 'pointer' : 'default',
                '&:hover': {
                  backgroundColor: 'grey.300'
                },
                width:"50%",
                transition: 'all 0.1s ease-in'
              }}
              key={row._id}
            >
              <StyledTableCell key={`${row._id}-cell0`}>{rowIndex + 1}</StyledTableCell>
              {columns.map(({ header, accessor, sx }, colIndex) => (
                <StyledTableCell sx={sx} key={`${row._id}-cell${colIndex + 1}`}>
                  {typeof accessor === 'function' ? (
                    accessor(row) || '-'
                  ) : header === 'Return_Qty' && showInputField ? (
                    <TextField
                      value={inputValues[row._id]?.[header] || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleInputChange(row._id, header, value);
                      }}
                      variant="outlined"
                      name="return_qty"
                      size="small"
                      sx={{ width: '60px' }}
                      type="number"
                      disabled={!selectedRows?.some((selected) => selected._id === row._id)}
                      InputProps={{
                        style: {
                          fontSize: '12px',
                          fontWeight: 700
                        }
                      }}
                    />
                  ) : header === 'Charges' && showInputFields ? (
                    <TextField
                      value={inputValues[row._id]?.[header] || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleInputChange(row._id, header, value);
                      }}
                      variant="outlined"
                      name="charges"
                      size="small"
                      sx={{ width: '100px' }}
                      type="number"
                      InputProps={{
                        style: {
                          fontSize: '12px',
                          fontWeight: 700
                        }
                      }}
                    />
                  ): header === 'Quantity' && showInputFields ? (
                    <TextField
                      value={inputValues[row._id]?.[header] || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleInputChange(row._id, header, value);
                      }}
                      variant="outlined"
                      name="quantity"
                      size="small"
                      sx={{ width: '100px' }}
                      type="number"
                      InputProps={{
                        style: {
                          fontSize: '12px',
                          fontWeight: 700
                        }
                      }}
                    />
                  ) : header === 'Amounts' ? (
                    (
                      parseFloat(inputValues[row._id]?.['Return_Qty'] || 0) *
                      parseFloat(row.mrp || 0) *
                      (1 - parseFloat(row.disc_per || 0) / 100)
                    ).toFixed(2) || '0.00'
                  ) : header === 'Disc Amt' ? (
                    (
                      (parseFloat(inputValues[row._id]?.['Return_Qty'] || 0) *
                        parseFloat(row.mrp || 0) *
                        parseFloat(row.disc_per || 0)) /
                      100
                    ).toFixed(2) || '0.00'
                  ) : (
                    <>
                      {header === 'Created At' || header === 'Date'
                        ? dateFormatter(getReccurssiveObjectKeys(row, accessor)) || '-'
                        : getReccurssiveObjectKeys(row, accessor) || '-'}
                    </>
                  )}
                </StyledTableCell>
              ))}
              {showOk && (
                <StyledTableCell>
                  <Checkbox
                    sx={{
                      transform: 'scale(0.8)',
                      padding: '0'
                    }}
                    name={row?._id}               
                    checked={isSelected(row, 'ok')}
                    onChange={() => handleRowSelect(row, 'ok')}
                    disabled={currentAction !== 'new' && currentAction !== 'edit'}
                  />
                </StyledTableCell>
              )}
              {showDone && (
                <StyledTableCell key={`${row._id}-edit-button-cell`}>
                  <CheckCircleIcon
                    onClick={() => handleDone(row._id)}
                    type="submit"
                    sx={{ fontSize: 25, color: '#1976D2', marginRight: '7%', cursor: 'pointer' }}
                  />
                  <CancelIcon
                    onClick={() => handleDefer(row._id)}
                    type="submit"
                    sx={{ fontSize: 25, color: 'red', cursor: 'pointer' }}
                  />
                </StyledTableCell>
              )}
              {showRevert && (
                <StyledTableCell key={`${row._id}-revert-button-cell`}>
                  <RefreshIcon
                    onClick={() => handleRevert(row._id)}
                    type="submit"
                    sx={{ fontSize: 25, color: '#1976D2', cursor: 'pointer' }}
                  />
                </StyledTableCell>
              )}
              {showAuthorize && (
                <StyledTableCell key={`${row._id}-cell-authorize`}>
                  <Button color="success" onClick={() => onAuthorizeHandler(row)}>
                    <SendIcon sx={{ fontSize: 24 }} /> 
                  </Button>
                  <Button color="error" onClick={() => cancelAppointment(row)}>
                    <CancelIcon sx={{ fontSize: 24 }} />
                  </Button>
                </StyledTableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};
